import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/common/Button';
import { Input } from '../components/common/Input';
import { Card, CardContent } from '../components/common/Card';
import type { LoginCredentials } from '../types';

export const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t } = useTranslation();
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginCredentials>();

  const onSubmit = async (data: LoginCredentials) => {
    setError('');
    setIsLoading(true);
    
    try {
      await login(data);
      console.log('Login successful, navigating to dashboard');
      navigate('/dashboard');
    } catch (err: any) {
      console.error('Login error:', err);
      setError(err.response?.data?.message || t('auth.invalidCredentials'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center mb-6">
          <img 
            src="/nodecrafts_leaveapp.svg" 
            alt="NodeCrafts LeaveApp" 
            className="h-20 w-auto"
          />
        </div>
        <h2 className="mt-6 text-center text-3xl font-bold text-gray-900">
{t('auth.signIn', 'Zaloguj się na swoje konto')}
        </h2>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <Card className="py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <CardContent>
            <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-800 rounded-md p-4 text-sm">
                  {error}
                </div>
              )}
              
              <Input
                label={t('auth.email', 'Adres email')}
                type="email"
                autoComplete="email"
                {...register('email', {
                  required: t('auth.emailRequired', 'Email jest wymagany'),
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: t('auth.invalidEmail', 'Nieprawidłowy adres email'),
                  },
                })}
                error={errors.email?.message}
              />

              <Input
                label={t('auth.password', 'Hasło')}
                type="password"
                autoComplete="current-password"
                {...register('password', {
                  required: t('auth.passwordRequired', 'Hasło jest wymagane'),
                })}
                error={errors.password?.message}
              />

              <div className="flex items-center justify-between">
                <div className="text-sm">
                  <Link to="/forgot-password" className="font-medium text-primary-600 hover:text-primary-500">
                    {t('auth.forgotPasswordLink', 'Zapomniałeś hasła?')}
                  </Link>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full"
                isLoading={isLoading}
              >
{t('auth.login', 'Zaloguj się')}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};